// auth/user/onboarding/components/OnboardingForm.jsx

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../../authContext';

import OnboardingNameStep from './steps/OnboardingNameStep';
import OnboardingProfessionalStep from './steps/OnboardingProfessionalStep';
import OnboardingExperienceStep from './steps/OnboardingExperienceStep';

import OnboardingProgress from './OnboardingProgress';
import { sanitizeUserUpdate } from '../../../../utils/userPayload';

const OnboardingForm = ({
  step,
  setStep,
  onComplete
}) => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    displayName: '',
    pronoun: '',
    countryOfResidence: '',
    communicationLanguage: '',

    profession: '',
    background: [],
    professionalRoles: [],
    voiceCompetenceLevel: '',
    mainUse: [],

    howDidYouHear: [],
  });

  useEffect(() => {
    if (!user) return;

    setFormData(prev => {
      if (prev.displayName) return prev;

      return {
        ...prev,
        displayName: user.displayName || '',
        pronoun: user.pronoun || '',
        countryOfResidence: user.countryOfResidence || '',
        communicationLanguage: user.communicationLanguage || '',

        profession: user.profession || '',
        background: user.background || [],
        professionalRoles: user.professionalRoles || [],
        voiceCompetenceLevel: user.voiceCompetenceLevel || '',
        mainUse: user.mainUse || [],

        howDidYouHear: user.howDidYouHear || [],
      };
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleArrayValue = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(v => v !== value)
        : [...prev[name], value]
    }));
  };

  const submit = async (payload) => {
    const token = localStorage.getItem('token');
    const cleanPayload = sanitizeUserUpdate(payload);

    const res = await axios.put(
      `${apiUrl}/users/me`,
      cleanPayload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const { reverify: _reverify, ...updatedUser } = res.data || {};
    setUser(currentUser => currentUser
      ? { ...currentUser, ...updatedUser }
      : currentUser
    );
    onComplete();
  };

  const handleFinish = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await submit({
        ...formData,
        onboardingCompleted: true
      });
    } catch (e) {
      console.error('Error completing onboarding', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      await submit({ onboardingCompleted: true });
    } catch (e) {
      console.warn('Error skipping onboarding', e);
    }
  };

  const normalizedFormData = {
    ...formData,
    professionalRoles: Array.isArray(formData.professionalRoles)
      ? formData.professionalRoles
      : [],
    mainUse: Array.isArray(formData.mainUse)
      ? formData.mainUse
      : [],
    howDidYouHear: Array.isArray(formData.howDidYouHear)
      ? formData.howDidYouHear
      : [],
  };

  const steps = [
    {
      id: 'about-you',
      component: (
        <OnboardingNameStep
          formData={formData}
          onChange={handleChange}
        />
      )
    },
    {
      id: 'background',
      component: (
        <OnboardingProfessionalStep
          formData={normalizedFormData}
          onChange={handleChange}
          toggleArrayValue={toggleArrayValue}
        />
      )
    },
    {
      id: 'experience',
      component: (
        <OnboardingExperienceStep
          formData={normalizedFormData}
          onChange={handleChange}
          toggleArrayValue={toggleArrayValue}
        />
      )
    }
  ];

  const saveDraft = async (partialData) => {
    try {
      const token = localStorage.getItem('token');

      const cleanPayload = sanitizeUserUpdate(partialData);

      await axios.put(
        `${apiUrl}/users/me`,
        cleanPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      console.warn('Autosave failed', e);
    }
  };

  return (
    <>
      <div className="app-modal-form">
        <OnboardingProgress
          step={step}
          totalSteps={steps.length}
        />

        {steps[step]?.component}

      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: step > 0 ? 'space-between' : 'flex-end',
          gap: '1rem',
          marginTop: '1.5rem'
        }}
      >
        {step > 0 && (
          <button
            className="app-btn app-btn-secondary"
            onClick={async () => {
              await saveDraft(formData);
              setStep(s => s - 1);
            }}
            disabled={isSubmitting}
          >
            {t('back')}
          </button>
        )}

        {step === 0 && (
          <button
            className="app-btn app-btn-secondary"
            onClick={handleSkip}
          >
            {t('skip_for_now', 'Skip for now')}
          </button>
        )}

        {step < steps.length - 1 ? (
          <button
            className="app-btn app-btn-primary"
            onClick={async () => {
              await saveDraft(formData);
              setStep(s => s + 1);
            }}
          >
            {t('next')}
          </button>
        ) : (
          <button
            className="app-btn app-btn-primary"
            onClick={handleFinish}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('saving', 'Saving')
              : t('finish_setup', 'Finish setup')}
          </button>
        )}
      </div>
    </>
  );
};

export default OnboardingForm;
